import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReqLabel } from "@/components/DiscoveryFormParts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, Lock } from "lucide-react";

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const Pick = ({ k, label, value, options, onChange, error }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-[#475569]"><ReqLabel text={label} /></Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={`q-select-${k}`} aria-invalid={!!error} className={`rounded-none mt-1.5 ${error ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"}`}><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
      <SelectContent className="max-h-72">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
    {error && <p data-testid={`q-err-${k}`} className="text-xs text-red-600 mt-1">Champ obligatoire.</p>}
  </div>
);

export default function CompanyIdentityStep({ form, setForm, set, reference, Fld, badKeys }) {
  const [loading, setLoading] = useState(false);

  const autofill = async () => {
    setLoading(true);
    try {
      const { data: c } = await api.get("/company-lookup", { params: { siret: form.siret } });
      setForm((f) => ({
        ...f,
        title: c.name || f.title, sector: c.sector || f.sector, region: c.region || f.region, city: c.city || f.city,
        year_founded: c.year_founded ?? f.year_founded, legal_form: c.legal_form, address: c.address, postal_code: c.postal_code,
        naf_code: c.naf_code, siret: c.siret || f.siret,
        revenue: c.revenue ?? f.revenue, net_result: c.net_result ?? f.net_result, employees: c.employees ?? f.employees,
        lat: c.lat ?? f.lat, lng: c.lng ?? f.lng, company_info: c,
      }));
      toast.success("Fiche pré-remplie à partir des données publiques (data.gouv.fr).");
      (c.warnings || []).forEach((w) => toast.warning(w));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Recherche impossible."); }
    setLoading(false);
  };

  const c = form.company_info;
  return (
    <div className="space-y-5">
      <div className="bg-[#F8F6F0] border border-[#E0C173]/40 p-4">
        <Label className="text-xs uppercase tracking-wider text-[#475569]">N° SIRET (ou SIREN) de l'entreprise</Label>
        <div className="mt-1.5 flex gap-2">
          <Input data-testid="q-field-siret" value={form.siret} onChange={set("siret")} placeholder="Ex. 504 980 822 00028" className="rounded-none border-gray-300 bg-white" inputMode="numeric" />
          <button type="button" data-testid="q-siret-autofill" onClick={autofill} disabled={loading || form.siret.replace(/\D/g, "").length < 9}
            className="shrink-0 inline-flex items-center gap-2 bg-[#1B2A4A] text-white px-4 text-sm hover:bg-[#2a3d63] disabled:opacity-40 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Remplir automatiquement
          </button>
        </div>
        <p className="mt-2 text-xs text-[#475569]">Données officielles issues de l'annuaire des entreprises (data.gouv.fr). Vous pourrez tout corriger.</p>
        {c && (
          <div data-testid="q-company-found" className="mt-3 text-sm text-[#1B2A4A] flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1 font-medium"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {c.name}</span>
            {c.legal_form && <span>{c.legal_form}</span>}
            {c.naf_code && <span>NAF {c.naf_code}</span>}
            {c.employees_label && <span>{c.employees_label}</span>}
            {c.revenue != null && <span>CA {c.finances_year} : {fmtEUR(c.revenue)}</span>}
            {c.dirigeants?.length > 0 && <span>Dirigeant : {c.dirigeants.join(", ")}</span>}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {Fld({ k: "title", label: "Nom / raison sociale *", placeholder: "Ex. SAS Menuiserie du Var" })}
        <Pick k="sector" label="Secteur d'activité *" value={form.sector} options={reference.sectors} error={badKeys.has("sector")} onChange={(v) => setForm((f) => ({ ...f, sector: v }))} />
        <Pick k="region" label="Région *" value={form.region} options={reference.regions} error={badKeys.has("region")} onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
        {Fld({ k: "city", label: "Ville *", placeholder: "Ex. Aix-en-Provence" })}
        {Fld({ k: "year_founded", label: "Année de création", type: "number", placeholder: "Ex. 1991" })}
        {Fld({ k: "legal_form", label: "Forme juridique", placeholder: "SAS, SARL…" })}
        <div className="sm:col-span-2">{Fld({ k: "address", label: "Adresse du siège", placeholder: "N°, rue, code postal, ville" })}</div>
      </div>
      <p className="text-xs text-gray-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Le nom, le SIRET et l'adresse restent confidentiels : seuls des éléments anonymisés seront publiés, après validation par PROAC Transmission.</p>
    </div>
  );
}
